import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, ShieldAlert, ShieldX, Loader2, RefreshCw, ChevronDown } from "lucide-react";

/**
 * Verified-Sender Badge — SPF / DKIM / DMARC status chips.
 *
 * At-a-glance panel that resolves the three DNS-level authentication
 * records for the Vinci sender domain and shows a green/yellow/red chip
 * for each.  Click a chip to reveal the raw TXT record + remediation note.
 */

const CHIPS = [
  { key: "spf",   label: "SPF"   },
  { key: "dkim",  label: "DKIM"  },
  { key: "dmarc", label: "DMARC" },
];

function ChipIcon({ ok, size = 12 }) {
  if (ok === true) return <ShieldCheck size={size} className="text-[#3DBB84]"/>;
  if (ok === false) return <ShieldX size={size} className="text-[#E5484D]"/>;
  return <ShieldAlert size={size} className="text-[#D8B23E]"/>;
}

function chipTone(ok) {
  if (ok === true) return { border: "border-[#3DBB84]/40", text: "text-[#3DBB84]", bg: "bg-[#3DBB84]/[0.06]" };
  if (ok === false) return { border: "border-[#E5484D]/40", text: "text-[#E5484D]", bg: "bg-[#E5484D]/[0.06]" };
  return { border: "border-[#D8B23E]/40", text: "text-[#D8B23E]", bg: "bg-[#D8B23E]/[0.06]" };
}

export function SenderVerificationBadge({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedKey, setExpandedKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/communications/sender-verification");
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not check sender"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const allGreen = !!data?.all_green;
  const bannerBorder = allGreen ? "border-[#3DBB84]/40" : "border-[#D8B23E]/40";
  const bannerAccent = allGreen ? "text-[#3DBB84]" : "text-[#D8B23E]";

  return (
    <div data-testid="sender-verification-badge"
         className={`border ${bannerBorder} rounded-sm bg-black/40 p-5 space-y-4`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-sm bg-white/[0.03] flex items-center justify-center ${bannerAccent}`}>
            <ChipIcon ok={allGreen ? true : (loading ? null : false)} size={18}/>
          </div>
          <div>
            <div className={`font-grotesk text-[10px] uppercase tracking-[0.28em] ${bannerAccent}`}>
              Verified Sender Badge
            </div>
            <div className="font-serif text-lg text-[#F5F5F0] mt-0.5">
              {data?.sender || "Loading sender…"}
            </div>
            <div className="font-body text-[11px] text-[#8A8A82] mt-0.5">
              Domain · <span className="text-[#F5F5F0] font-mono">{data?.domain || "—"}</span>
              {data?.checked_at && (
                <> · Checked {new Date(data.checked_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</>
              )}
            </div>
          </div>
        </div>
        <button data-testid="sender-verification-refresh"
                onClick={load} disabled={loading}
                className="text-[#8A8A82] hover:text-[#F5F5F0] p-1">
          {loading ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
        </button>
      </div>

      {/* Chips */}
      <div className="grid sm:grid-cols-3 gap-2">
        {CHIPS.map(({ key, label }) => {
          const chk = data?.[key];
          const ok = loading ? null : (chk?.ok ?? false);
          const tone = chipTone(ok);
          const status = chk?.status || (loading ? "checking…" : "unknown");
          const isOpen = expandedKey === key;
          return (
            <button key={key}
              data-testid={`sender-chip-${key}`}
              onClick={() => setExpandedKey(isOpen ? null : key)}
              className={`text-left border ${tone.border} ${tone.bg} rounded-sm px-3 py-2.5 transition-colors hover:bg-white/[0.03]`}>
              <div className="flex items-center gap-2">
                <ChipIcon ok={ok}/>
                <span className={`font-grotesk text-[10px] uppercase tracking-[0.28em] ${tone.text}`}>
                  {label}
                </span>
                <span className="ml-auto font-body text-[10px] text-[#8A8A82] uppercase tracking-wider">
                  {status}
                </span>
                <ChevronDown size={12} className={`text-[#8A8A82] transition-transform ${isOpen ? "rotate-180" : ""}`}/>
              </div>
            </button>
          );
        })}
      </div>

      {/* Expanded detail */}
      <AnimatePresence>
        {expandedKey && data?.[expandedKey] && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden">
            <div data-testid={`sender-chip-detail-${expandedKey}`}
                 className="border border-white/10 rounded-sm bg-[#0a0a0a] p-3 space-y-2">
              <div className="font-body text-[12px] text-[#F5F5F0]">
                {data[expandedKey].note}
              </div>
              {data[expandedKey].selector && (
                <div className="font-body text-[11px] text-[#8A8A82]">
                  Selector · <span className="font-mono text-[#2FE4C6]">{data[expandedKey].selector}._domainkey.{data.domain}</span>
                </div>
              )}
              {data[expandedKey].policy && (
                <div className="font-body text-[11px] text-[#8A8A82]">
                  Policy · <span className="font-mono text-[#D8B23E]">p={data[expandedKey].policy}</span>
                </div>
              )}
              {data[expandedKey].record ? (
                <pre className="font-mono text-[11px] text-[#C9C9C2] bg-black/50 border border-white/5 rounded-sm p-2 overflow-x-auto whitespace-pre-wrap break-all">
{data[expandedKey].record}
                </pre>
              ) : (
                <div className="font-body text-[11px] text-[#6f6f68] italic">
                  No TXT record found for this check.
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
